import { Grid, IconButton, Menu, MenuItem, ListItemIcon, Divider } from '@mui/material';
import { useEffect, useState } from 'react';
import DataTable from 'react-data-table-component';
import { Link, useNavigate } from 'react-router-dom';
import { Search } from '../../Components';
import {
   useDeleteContractTypeMutation,
   useToggleContractTypeStatusMutation,
   useGetPaginatedContractTypesQuery,
} from '../../Redux/Service/ContractTypeApi';
import Loading from '../../Components/Loading';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PostAddIcon from '@mui/icons-material/PostAdd';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import StyledDashboardLayout from '../style';

function ContractTypeList() {
   const MySwal = withReactContent(Swal);
   const navigate = useNavigate();
   const [searchQuery, setSearchQuery] = useState('');
   const [anchorEl, setAnchorEl] = useState(null);
   const [menuRow, setMenuRow] = useState(null);
   const [page, setPage] = useState(1);
   const [rowsPerPage, setRowsPerPage] = useState(10);

   const {
      data: contractTypesData,
      isLoading,
      refetch,
   } = useGetPaginatedContractTypesQuery({ limit: rowsPerPage, page });
   const [deleteContractType] = useDeleteContractTypeMutation();
   const [toggleStatus] = useToggleContractTypeStatusMutation();

   const contractTypes = contractTypesData?.response?.data || [];
   const totalCount = contractTypesData?.response?.total || 0;
   const customStyles = {
      rows: {
         style: {
            margin: '0px',
            padding: 0,
         },
      },
      headCells: {
         style: {
            margin: '0px',
            padding: '10px 15px',
            fontWeight: 600,
            fontSize: 15,
            background: '#3F30FF1A',
         },
      },
      cells: {
         style: {
            margin: '0px',
            padding: '10px 15px',
            fontSize: 15,
         },
      },
   };

   const openMenuForRow = (event, row) => {
      setAnchorEl(event.currentTarget);
      setMenuRow(row);
   };

   const closeMenu = () => {
      setAnchorEl(null);
      setMenuRow(null);
   };

   const handleEdit = (id) => {
      closeMenu();
      navigate(`/contract-type/edit/${id}`);
   };

   const handleDelete = async (id, name) => {
      closeMenu();
      const result = await MySwal.fire({
         title: 'Are you sure?',
         html: `You are about to delete the contract type: <strong>${name}</strong>.<br/>This action cannot be undone.`,
         icon: 'warning',
         showCancelButton: true,
         confirmButtonColor: '#7066e0',
         cancelButtonColor: '#6e7881',
         confirmButtonText: 'Yes, delete it!',
         cancelButtonText: 'Cancel',
      });

      if (result.isConfirmed) {
         try {
            const response = await deleteContractType(id).unwrap();
            toast.success(response?.message || 'Contract type deleted successfully');
            refetch();
         } catch (error) {
            const errorMsg =
               error?.data?.error || error?.data?.message || 'Failed to delete contract type';
            toast.error(errorMsg);
         }
      }
   };

   const handleToggleStatus = async (id, currentStatus, name) => {
      closeMenu();
      const action = currentStatus ? 'deactivate' : 'activate';
      const result = await MySwal.fire({
         title: `${action.charAt(0).toUpperCase() + action.slice(1)} Contract Type?`,
         html: `Are you sure you want to ${action} <strong>${name}</strong>?`,
         icon: 'question',
         showCancelButton: true,
         confirmButtonColor: currentStatus ? '#3F30FF' : '#28a745',
         cancelButtonColor: '#6c757d',
         confirmButtonText: `Yes, ${action}`,
         cancelButtonText: 'Cancel',
      });

      if (result.isConfirmed) {
         try {
            const response = await toggleStatus(id).unwrap();
            toast.success(response?.message || `Contract type ${action}d successfully`);
            refetch();
         } catch (error) {
            const errorMsg =
               error?.data?.error || error?.data?.message || `Failed to ${action} contract type`;
            toast.error(errorMsg);
         }
      }
   };

   const handleSearch = (search) => {
      setSearchQuery(search);
      setPage(1);
   };

   const filteredData = contractTypes.filter((item) => {
      const query = searchQuery.toLowerCase();
      return (
         item.name?.toLowerCase().includes(query) || item.description?.toLowerCase().includes(query)
      );
   });

   const columns = [
      {
         name: 'SL No',
         cell: (row, index) => index + 1,
         width: '120px',
      },
      {
         name: 'Name',
         selector: (row) => row.name,
         sortable: true,
         wrap: true,
         cell: (row) => (
            <div style={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>{row.name}</div>
         ),
      },
      {
         name: 'Description',
         selector: (row) => row.description || 'N/A',
         sortable: true,
         wrap: true,
         cell: (row) => (
            <div style={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
               {(row.description && row.description.slice(0, 50) + '...') || 'N/A'}
            </div>
         ),
      },
      {
         name: 'Status',
         selector: (row) => (row.is_active ? 'Active' : 'Inactive'),
         sortable: true,
         width: '120px',
         cell: (row) => (
            <span
               style={{
                  padding: '4px 8px',
                  borderRadius: '4px',
                  backgroundColor: row.is_active ? '#d4edda' : '#f8d7da',
                  color: row.is_active ? '#155724' : '#721c24',
                  fontSize: '12px',
                  fontWeight: '500',
               }}
            >
               {row.is_active ? 'Active' : 'Inactive'}
            </span>
         ),
      },
      {
         name: 'Action',
         cell: (row) => (
            <div>
               <IconButton aria-label="more" onClick={(e) => openMenuForRow(e, row)}>
                  <MoreVertIcon />
               </IconButton>
               <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl) && menuRow?.id === row.id}
                  onClose={closeMenu}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                  PaperProps={{
                     sx: { borderRadius: 2, boxShadow: 3, minWidth: 170 },
                  }}
               >
                  <MenuItem
                     onClick={() => navigate(`/contract-type/view/${row.id}`)}
                     sx={{ gap: 1 }}
                  >
                     <ListItemIcon>
                        <VisibilityIcon fontSize="small" />
                     </ListItemIcon>
                     View Details
                  </MenuItem>
                  <MenuItem onClick={() => handleEdit(row.id)} sx={{ gap: 1 }}>
                     <ListItemIcon>
                        <EditIcon fontSize="small" />
                     </ListItemIcon>
                     Edit
                  </MenuItem>
                  <MenuItem
                     onClick={() => handleToggleStatus(row.id, row.is_active, row.name)}
                     sx={{ gap: 1 }}
                  >
                     <ListItemIcon>{row.is_active ? '🔒' : '🔓'}</ListItemIcon>
                     {row.is_active ? 'Deactivate' : 'Activate'}
                  </MenuItem>
                  <Divider />
                  <MenuItem onClick={() => handleDelete(row.id, row.name)} sx={{ gap: 1 }}>
                     <ListItemIcon>
                        <DeleteIcon fontSize="small" color="error" />
                     </ListItemIcon>
                     <span style={{ color: '#dc3545' }}>Delete</span>
                  </MenuItem>
               </Menu>
            </div>
         ),
         width: '100px',
      },
   ];

   if (isLoading) return <Loading />;

   return (
      <StyledDashboardLayout>
         <div className="content-wrapper">
            <div className="content-header">
               <div className="container-fluid">
                  <div className="d-flex justify-content-between mb-2">
                     <div className="pageTitle">
                        <h1 className="m-0">Contract Types</h1>
                     </div>
                     <div className="btnWrap">
                        <Link to="/contract-type/add" className="btn addBtn">
                           <PostAddIcon /> Add Contract Type
                        </Link>
                     </div>
                  </div>
               </div>
            </div>
            <div className="card m-3 customCard">
               <div>
                  <Grid container spacing={2}>
                     <Grid item md={6} xs={12}></Grid>
                     <Grid item md={6} xs={12} display="flex" justifyContent="flex-end">
                        <Search onSearch={(query) => handleSearch(query)} />
                     </Grid>
                  </Grid>

                  <DataTable
                     columns={columns}
                     data={filteredData}
                     customStyles={customStyles}
                     className="dataTable"
                     pagination
                     paginationServer
                     paginationTotalRows={totalCount}
                     paginationDefaultPage={page}
                     paginationPerPage={rowsPerPage}
                     paginationRowsPerPageOptions={[10, 25, 50, 100]}
                     onChangePage={(newPage) => setPage(newPage)}
                     onChangeRowsPerPage={(newRowsPerPage, newPage) => {
                        setRowsPerPage(newRowsPerPage);
                        setPage(newPage);
                     }}
                  />
               </div>
            </div>
         </div>
      </StyledDashboardLayout>
   );
}

export default ContractTypeList;
